<!-- Custom Elements: form-associated -->
<script lang="ts">
  import Icon from '$components/icon/icon.svelte';
  import type { Component, Snippet } from 'svelte';
  import Loading from '$components/loading/loading.svelte';
  import type { HTMLButtonAttributes } from 'svelte/elements'
  import { useHostAttributes } from '$composables/use-host-attributes.svelte.ts';

  interface Props {
    variant?: 'primary' | 'secondary' | 'tertiary' | 'destructive';
    size?: 'sm' | 'medium' | 'large';
    disabled?: boolean;
    iconOnly?: boolean;
    leadingIcon?: Component | string;
    trailingIcon?: Component | string;
    loading?: boolean;
    loadingLabel?: string;
    forceHover?: boolean;
    forcePressed?: boolean;
    onclick?: () => void;
  }

  let hostAttrs = useHostAttributes(() => $host());

  let svelteProps: Props & HTMLButtonAttributes = $props();

  let combinedProps = $derived({ ...svelteProps, ...hostAttrs.current });

  let {
    variant = 'primary',
    size = 'medium',
    disabled = false,
    loading = false,
    type = false,
    onclick,
    class: className,

    // Fallbacks for web components, attributes are case insensitive
    iconOnly = hostAttrs.current.icononly === "true",
    leadingIcon = hostAttrs.current.leadingicon,
    trailingIcon = hostAttrs.current.trailingicon,
    loadingLabel = hostAttrs.current.loadinglabel ?? 'Loading...',
    forceHover = hostAttrs.current.forcehover === "true",
    forcePressed = hostAttrs.current.forcepressed === "true",

    ...restProps
  }: Props & HTMLButtonAttributes = $derived(combinedProps);

  let iconSize = $derived(size === 'sm' ? 14 : size === 'large' ? 20 : 16);
  let loadingVariant = $derived(
    (variant === 'primary' || variant === 'destructive') && !disabled ? 'white' : 'color'
  );
  let loadingSize = $derived(size === 'sm' ? '0.875rem' : size === 'large' ? '1.25rem' : '1rem');

  function handleClick(evt: MouseEvent) {
    if(!$host()) onclick?.(evt);
  }
</script>


<svelte:element
  this="button"
  class="button a2u-ui-{variant} a2u-ui-{size} {className}"
  class:icon-only={iconOnly}
  class:force-hover={forceHover}
  class:force-pressed={forcePressed}
  class:loading
  {disabled}
  onclick={handleClick}
  {...restProps}>
  {#if loading}
    <Loading size={loadingSize} variant={loadingVariant} />
    {#if !iconOnly}<span>{loadingLabel}</span>{/if}
  {:else}
    {#if leadingIcon}<Icon icon={leadingIcon} size={iconSize} />{/if}
    {#if !iconOnly}<span class="label"><slot /></span>{/if}
    {#if trailingIcon && !iconOnly}<Icon icon={trailingIcon} size={iconSize} />{/if}
  {/if}
</svelte:element>

<style lang="scss">
  .button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-style: solid;
    border-width: var(--a2u-ui-border-width-base-sm);
    border-radius: var(--a2u-ui-control-border-radius-size-base-standard);
    cursor: pointer;
    font-family: var(--a2u-ui-core-font-family-body);
    white-space: nowrap;
    text-decoration: none;

    &:disabled {
      cursor: not-allowed;
    }
  }

  .a2u-ui-primary {
    background: var(--a2u-ui-button-brand-color-bg-strong-base);
    border-color: var(--a2u-ui-button-brand-color-border-strong-base);
    color: var(--a2u-ui-button-brand-color-text-strong-base);

    &:hover:not(:disabled):not(.loading), &.force-hover:not(:disabled):not(.loading) {
      background: var(--a2u-ui-button-brand-color-bg-strong-hover);
      border-color: var(--a2u-ui-button-brand-color-border-strong-hover);
      color: var(--a2u-ui-button-brand-color-text-strong-hover);
    }

    &:active:not(:disabled):not(.loading), &.force-pressed:not(:disabled):not(.loading) {
      background: var(--a2u-ui-button-brand-color-bg-strong-pressed);
      border-color: var(--a2u-ui-button-brand-color-border-strong-pressed);
      color: var(--a2u-ui-button-brand-color-text-strong-pressed);
    }

    &:disabled {
      background: var(--a2u-ui-button-brand-color-bg-strong-disabled);
      border-color: var(--a2u-ui-button-brand-color-border-strong-disabled);
      color: var(--a2u-ui-button-brand-color-text-strong-disabled);
    }

    &.loading {
      background: var(--a2u-ui-button-brand-color-bg-strong-loading);
      border-color: var(--a2u-ui-button-brand-color-border-strong-loading);
      color: var(--a2u-ui-button-brand-color-text-strong-loading);
    }
  }

  .a2u-ui-secondary {
    background: var(--a2u-ui-button-brand-color-bg-standard-base);
    border-color: var(--a2u-ui-button-brand-color-border-standard-base);
    color: var(--a2u-ui-color-text-interactive-standard-default);

    &:hover:not(:disabled):not(.loading), &.force-hover:not(:disabled):not(.loading) {
      background: var(--a2u-ui-button-brand-color-bg-standard-hover);
      border-color: var(--a2u-ui-button-brand-color-border-standard-hover);
      color: var(--a2u-ui-color-text-interactive-standard-default);
    }

    &:active:not(:disabled):not(.loading), &.force-pressed:not(:disabled):not(.loading) {
      background: var(--a2u-ui-button-brand-color-bg-standard-pressed);
      border-color: var(--a2u-ui-button-brand-color-border-standard-pressed);
      color: var(--a2u-ui-color-text-interactive-standard-default);
    }

    &:disabled {
      background: var(--a2u-ui-button-brand-color-bg-standard-disabled);
      border-color: var(--a2u-ui-button-brand-color-border-standard-disabled);
      color: var(--a2u-ui-button-brand-color-text-standard-disabled);
    }

    &.loading {
      background: var(--a2u-ui-button-brand-color-bg-standard-loading);
      border-color: var(--a2u-ui-button-brand-color-border-standard-loading);
      color: var(--a2u-ui-core-color-brand-700);
    }
  }

  .a2u-ui-tertiary {
    background: var(--a2u-ui-button-brand-color-bg-subtle-base);
    border-color: var(--a2u-ui-button-brand-color-border-subtle-base);
    color: var(--a2u-ui-core-color-blue-600);

    &:hover:not(:disabled):not(.loading), &.force-hover:not(:disabled):not(.loading) {
      background: var(--a2u-ui-button-brand-color-bg-subtle-hover);
      border-color: var(--a2u-ui-button-brand-color-border-subtle-hover);
      color: var(--a2u-ui-core-color-blue-600);
    }

    &:active:not(:disabled):not(.loading), &.force-pressed:not(:disabled):not(.loading) {
      background: var(--a2u-ui-button-brand-color-bg-subtle-pressed);
      border-color: var(--a2u-ui-button-brand-color-border-subtle-pressed);
      color: var(--a2u-ui-core-color-blue-600);
    }

    &:disabled {
      background: var(--a2u-ui-button-brand-color-bg-subtle-disabled);
      border-color: var(--a2u-ui-button-brand-color-border-subtle-disabled);
      color: var(--a2u-ui-button-brand-color-text-subtle-disabled);
    }

    &.loading {
      background: var(--a2u-ui-button-brand-color-bg-subtle-loading);
      border-color: var(--a2u-ui-button-brand-color-border-subtle-loading);
      color: var(--a2u-ui-core-color-blue-700);
    }
  }

  .a2u-ui-destructive {
    background: var(--a2u-ui-button-destructive-color-bg-strong-base);
    border-color: var(--a2u-ui-button-destructive-color-border-strong-base);
    color: var(--a2u-ui-button-destructive-color-text-strong-base);

    &:hover:not(:disabled):not(.loading), &.force-hover:not(:disabled):not(.loading) {
      background: var(--a2u-ui-button-destructive-color-bg-strong-hover);
      border-color: var(--a2u-ui-button-destructive-color-border-strong-hover);
      color: var(--a2u-ui-button-destructive-color-text-strong-hover);
    }

    &:active:not(:disabled):not(.loading), &.force-pressed:not(:disabled):not(.loading) {
      background: var(--a2u-ui-button-destructive-color-bg-strong-pressed);
      border-color: var(--a2u-ui-button-destructive-color-border-strong-pressed);
      color: var(--a2u-ui-button-destructive-color-text-strong-pressed);
    }

    &:disabled {
      background: var(--a2u-ui-button-destructive-color-bg-strong-disabled);
      border-color: var(--a2u-ui-button-destructive-color-border-strong-disabled);
      color: var(--a2u-ui-button-destructive-color-text-strong-disabled);
    }

    &.loading {
      background: var(--a2u-ui-button-destructive-color-bg-strong-loading);
      border-color: var(--a2u-ui-button-destructive-color-border-strong-loading);
      color: var(--a2u-ui-button-destructive-color-text-strong-loading);
    }
  }

  .a2u-ui-sm {
    min-height: var(--a2u-ui-core-sizing-32);
    padding: 0 var(--a2u-ui-space-around-base-sm);
    gap: var(--a2u-ui-space-between-base-xs);
    font: var(--a2u-ui-font-sm-title-strong);
  }

  .a2u-ui-medium {
    min-height: var(--a2u-ui-core-sizing-40);
    padding: 0 var(--a2u-ui-space-around-base-md);
    gap: var(--a2u-ui-space-between-base-sm);
    font: var(--a2u-ui-font-body-title-strong);
  }

  .a2u-ui-large {
    min-height: var(--a2u-ui-core-sizing-48);
    padding: 0 var(--a2u-ui-space-around-base-lg);
    gap: var(--a2u-ui-space-between-base-sm);
    font: var(--a2u-ui-font-body-title-strong);
  }

  :global([data-theme=legacy]) {
    .a2u-ui-sm, .a2u-ui-medium, .a2u-ui-large {
      font-weight: 400;
    }
  }

  .icon-only {
    padding: 0;

    &.a2u-ui-sm {
      width: var(--a2u-ui-core-sizing-32);
      height: var(--a2u-ui-core-sizing-32);
      min-height: unset;
    }

    &.a2u-ui-medium {
      width: var(--a2u-ui-core-sizing-40);
      height: var(--a2u-ui-core-sizing-40);
      min-height: unset;
    }

    &.a2u-ui-large {
      width: var(--a2u-ui-core-sizing-48);
      height: var(--a2u-ui-core-sizing-48);
      min-height: unset;
    }
  }
</style>
